import React, { useState, useRef, useEffect } from "react";
import { Loader2, FileText, Sparkles, CheckCircle2 } from "lucide-react";

export function useDocumentAnalysisProgress() {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [stepText, setStepText] = useState("");
  const [docName, setDocName] = useState("");
  const intervalRef = useRef(null);

  const startProgress = (fileName) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setDocName(fileName || "");
    setProgress(12);
    setStatus("Uploading document & initializing vision OCR...");
    setStepText("Step 1 of 4: Uploading");

    const startTime = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      if (elapsed < 2.5) {
        setProgress(Math.min(32, 12 + elapsed * 8));
        setStatus("Uploading document & initializing vision OCR...");
        setStepText("Step 1 of 4: Uploading");
      } else if (elapsed < 5.5) {
        setProgress(Math.min(65, 32 + (elapsed - 2.5) * 11));
        setStatus("Scanning document text, merchant & charges...");
        setStepText("Step 2 of 4: Multimodal OCR");
      } else if (elapsed < 9) {
        setProgress(Math.min(86, 65 + (elapsed - 5.5) * 6));
        setStatus("Matching property address & Schedule E category...");
        setStepText("Step 3 of 4: Property matching");
      } else {
        setProgress((prev) => Math.min(94, prev + 0.35));
        setStatus("Structuring itemized breakdown & tax parameters...");
        setStepText("Step 4 of 4: Finalizing breakdown");
      }
    }, 250);
  };

  const completeProgress = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setProgress(100);
    setStatus("Document analysis complete!");
    setStepText("Complete");
  };

  const resetProgress = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setProgress(0);
    setStatus("");
    setStepText("");
    setDocName("");
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return {
    progress,
    status,
    stepText,
    docName,
    startProgress,
    completeProgress,
    resetProgress,
  };
}

export default function DocumentAnalysisProgress({
  progress = 0,
  status = "Analyzing document with Gemini...",
  stepText = "",
  docName = "",
  isOverlay = false,
}) {
  const isComplete = progress >= 100;

  const content = (
    <div
      className="py-4 px-3 sm:px-6 flex flex-col items-center justify-center gap-3 w-full animate-fadeIn text-center"
      data-testid="ai-analyzing-progress"
    >
      {/* Spinner / Success Icon + Main Status */}
      <div className="flex items-center justify-center gap-2 text-teal-600 dark:text-teal-400">
        {isComplete ? (
          <CheckCircle2 size={24} className="text-emerald-500 animate-bounce" />
        ) : (
          <Loader2 size={22} className="animate-spin text-teal-500 shrink-0" />
        )}
        <span className="text-sm font-bold tracking-tight text-slate-900 dark:text-slate-100">
          {status || "Analyzing receipt with Gemini Flash..."}
        </span>
      </div>

      {/* Document filename badge */}
      {(docName || stepText) && (
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 text-teal-700 dark:text-teal-300 text-xs font-medium border border-teal-500/20 max-w-full">
          {docName && (
            <span className="flex items-center gap-1 truncate max-w-[220px] sm:max-w-xs">
              <FileText size={13} className="shrink-0 text-teal-500" />
              <span className="truncate">{docName}</span>
            </span>
          )}
          {docName && stepText && <span className="text-teal-400/50">•</span>}
          {stepText && (
            <span className="font-semibold text-teal-600 dark:text-teal-400">{stepText}</span>
          )}
        </div>
      )}

      {/* Progress Bar Container */}
      <div className="w-full max-w-md mx-auto space-y-1.5 mt-1">
        <div className="flex justify-between items-center text-xs font-semibold text-slate-600 dark:text-slate-400 px-0.5">
          <span className="flex items-center gap-1.5">
            <Sparkles size={13} className="text-teal-500" />
            <span>Analysis Progress</span>
          </span>
          <span className="text-teal-600 dark:text-teal-400 font-bold tabular-nums font-mono">
            {Math.round(progress)}%
          </span>
        </div>

        <div className="h-2.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden p-0.5 shadow-inner">
          <div
            className={`h-full rounded-full transition-all duration-300 ease-out shadow-sm ${
              isComplete
                ? "bg-emerald-500"
                : "bg-gradient-to-r from-teal-500 via-emerald-400 to-teal-400"
            }`}
            style={{ width: `${Math.min(100, Math.max(5, progress))}%` }}
          />
        </div>
      </div>

      {/* Subtitle helper description */}
      <p className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center justify-center gap-1.5 text-center mt-1">
        {!isComplete && (
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal-500 animate-ping shrink-0" />
        )}
        <span>Extracting line items, vendor, property address, and deductible totals</span>
      </p>
    </div>
  );

  if (isOverlay) {
    return (
      <div className="absolute inset-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm flex flex-col items-center justify-center p-4 z-20 rounded-2xl animate-fadeIn">
        <div className="max-w-md w-full p-5 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-teal-500/30">
          {content}
        </div>
      </div>
    );
  }

  return content;
}
